import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

/**
 * Module-level cache — survives across re-renders and multiple hook consumers.
 * Supabase + ipapi.co are each called AT MOST ONCE per browser session.
 * No Realtime subscription, no polling, no refetch on re-mount.
 */
let cache = null          // { countries, detectedCode, detectedName } once resolved
let fetchPromise = null   // in-flight promise — prevents duplicate parallel fetches

async function loadGeoData() {
  // Already resolved — return immediately, zero network calls
  if (cache) return cache

  // Already in flight — wait for the same promise, don't fire a second request
  if (fetchPromise) return fetchPromise

  fetchPromise = (async () => {
    // 1. Fetch country list + legal ages from Supabase (one-time read, no subscription)
    const { data: countryRows, error: dbError } = await supabase
      .from('country_age_limits')
      .select('country_code, country_name, dial_code, legal_age')
      .order('country_name')

    if (dbError) throw new Error(dbError.message)
    const countries = countryRows || []

    // 2. Detect country from IP — silent, 5s timeout, falls back to ZM on any failure
    let detectedCode = ''
    let detectedName = ''
    try {
      const res = await fetch('https://ipapi.co/json/', {
        signal: AbortSignal.timeout(5000),
      })
      if (res.ok) {
        const geo = await res.json()
        const code = geo.country_code || 'ZM'
        const matched = countries.find(c => c.country_code === code)
        detectedCode = code
        detectedName = matched?.country_name || geo.country_name || ''
      }
    } catch {
      // Silently fall back — user sees nothing, default ZM applies
    }

    cache = { countries, detectedCode, detectedName }
    return cache
  })()

  return fetchPromise
}

/**
 * useGeoAge — returns country list + IP-detected country for age validation.
 *
 * Safe to call from multiple components — only one Supabase read and
 * one ipapi.co call ever happen per session, no matter how many times
 * this hook mounts or re-renders.
 */
export function useGeoAge() {
  const [data, setData] = useState(cache) // if already cached, render instantly
  const [geoLoading, setGeoLoading] = useState(!cache)
  const [geoError, setGeoError] = useState(null)

  useEffect(() => {
    // Already cached from a previous mount — nothing to do
    if (cache) {
      setData(cache)
      setGeoLoading(false)
      return
    }

    let cancelled = false
    loadGeoData()
      .then(result => {
        if (!cancelled) {
          setData(result)
          setGeoLoading(false)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setGeoError(err.message)
          setGeoLoading(false)
        }
      })

    return () => { cancelled = true }
  }, []) // empty dep array — runs once per mount, short-circuits immediately if cached

  const countries = data?.countries || []
  const detectedCode = data?.detectedCode || ''
  const detectedName = data?.detectedName || ''

  const getLegalAge = (code) => {
    const row = countries.find(c => c.country_code === code)
    return row?.legal_age ?? 18
  }

  return { countries, detectedCode, detectedName, getLegalAge, geoLoading, geoError }
}